import { ippLocalStorage } from '../../../../utils/local-storage/ippLocalStorage'

export const SESSION_DATA_IDENTIFIER = 'session'

// ---------------------------------------------------------------------------
// Session-change pub/sub
//
// Shared between sessionToken.utils (imperative callers like logout) and
// useSessionData.hooks (React hook via useSyncExternalStore).
//
// Any code that mutates the session in localStorage MUST call
// notifySessionChange() afterward so every useSessionData consumer re-renders
// with the latest value.
// ---------------------------------------------------------------------------
const sessionListeners = new Set<() => void>()

/** Subscribe to session-data changes (used by useSyncExternalStore). */
export function subscribeToSessionChanges(listener: () => void): () => void {
  sessionListeners.add(listener)
  return () => {
    sessionListeners.delete(listener)
  }
}

/** Notify all subscribers that session data changed in localStorage. */
export function notifySessionChange(): void {
  sessionListeners.forEach(listener => listener())
}

/**
 * Synchronously gets session data from localStorage.
 * Returns null if no data exists or if parsing fails.
 */
export const getSessionFromStorage = (): { token?: string } | null => {
  const data = localStorage.getItem(SESSION_DATA_IDENTIFIER)
  if (data) {
    try {
      return JSON.parse(data)
    } catch {
      return null
    }
  }
  return null
}

/**
 * Synchronously sets session data in localStorage.
 */
export const setSessionInStorage = (sessionData: { token: string }): void => {
  ippLocalStorage.setItem(SESSION_DATA_IDENTIFIER, JSON.stringify(sessionData))
}

/**
 * Removes session data from localStorage and notifies React subscribers.
 * Used during logout and SSO re-auth flows.
 */
export const removeSessionData = (): void => {
  localStorage.removeItem(SESSION_DATA_IDENTIFIER)
  notifySessionChange()
}
